import { useEffect, useState } from "react";
import { Flame, Beef, Wheat, Droplet, ChefHat, Star, Wine, Utensils, Pencil, Check, X, StickyNote, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { isFavorite, addFavorite, removeFavorite, loadProfile } from "@/lib/mbStorage";
import { getDietaryWarnings } from "@/lib/mbNutrition";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MacroPill = ({ Icon, label, value, color }) => (
  <div className="flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl bg-slate-50 border border-slate-100">
    <Icon size={16} style={{ color }} />
    <span className="text-sm font-bold text-slate-800">{value}g</span>
    <span className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">{label}</span>
  </div>
);

const EditField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-[9px] uppercase tracking-wide text-slate-400 font-bold mb-1">{label}</label>
    <input
      type="number"
      min="0"
      inputMode="numeric"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#2b90ef]"
    />
  </div>
);

const fmtTime = (ts) =>
  new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

export const MealDetail = ({ meal, onClose, onOpenRecipe, onSaveEdit }) => {
  const open = !!meal;
  const [fav, setFav] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ calories: 0, protein: 0, carbs: 0, fat: 0, alcohol_g: 0, note: "" });
  const isDrink = meal && (meal.is_drink || meal.kind === "drink" || meal.meal_type === "Bevanda");
  const profile = loadProfile();
  const warnings = meal ? getDietaryWarnings(profile, [meal.dish_name, meal.drink_category, ...(meal.ingredients || [])]) : [];

  useEffect(() => {
    if (meal) {
      setFav(isFavorite(meal.id));
      setEditing(false);
    }
  }, [meal?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = () => {
    setForm({
      calories: meal.calories || 0,
      protein: meal.protein || 0,
      carbs: meal.carbs || 0,
      fat: meal.fat || 0,
      alcohol_g: meal.alcohol_g || 0,
      note: meal.note || "",
    });
    setEditing(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveEdit = () => {
    const num = (v) => Math.max(0, parseInt(v, 10) || 0);
    const patch = { calories: num(form.calories), protein: num(form.protein), carbs: num(form.carbs), fat: num(form.fat), note: (form.note || "").trim() };
    if (isDrink) patch.alcohol_g = Math.max(0, parseFloat(form.alcohol_g) || 0);
    onSaveEdit?.(meal.id, patch);
    setEditing(false);
    toast.success("Pasto aggiornato", { description: `${patch.calories} kcal` });
  };

  const toggleFav = async () => {
    if (!meal || saving) return;
    if (fav) {
      removeFavorite(meal.id);
      setFav(false);
      toast("Rimossa dai preferiti");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/recipe`, {
        dish_name: meal.dish_name,
        ingredients: meal.ingredients || null,
      });
      addFavorite({ id: meal.id, dish_name: meal.dish_name, image: meal.image || null, recipe: data });
      setFav(true);
      toast.success("Salvata tra i preferiti ⭐", { description: data.title });
    } catch (err) {
      toast.error("Impossibile salvare", {
        description: err?.response?.data?.detail || "Riprova più tardi",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DrawerContent className="max-w-[440px] mx-auto" data-testid="meal-detail-sheet">
        {meal && (
          <div className="px-5 pb-8 pt-2 max-h-[82vh] overflow-y-auto">
            {meal.image ? (
              <img src={meal.image} alt={meal.dish_name} className="w-full h-44 object-cover rounded-2xl mb-4" />
            ) : (
              <div className="w-full h-44 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                {isDrink ? <Wine size={40} className="text-[#2cd4bf]" /> : <Utensils size={40} className="text-slate-300" />}
              </div>
            )}

            <DrawerHeader className="p-0 mb-4">
              <div className="flex items-start justify-between gap-3">
                <div className="text-left">
                  <span className="text-[10px] uppercase tracking-wide text-[#2cd4bf] font-bold">
                    {meal.meal_type} · {fmtTime(meal.timestamp)}
                  </span>
                  <DrawerTitle className="font-display text-xl font-bold text-slate-800 leading-tight" data-testid="detail-dish-name">
                    {meal.dish_name}
                  </DrawerTitle>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center gap-1 text-[#f59e0b] font-bold text-2xl font-display" data-testid="detail-calories">
                    <Flame size={20} />{meal.calories}
                  </div>
                  <span className="text-[10px] text-slate-400">kcal</span>
                </div>
              </div>
            </DrawerHeader>

            {warnings.length > 0 && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5 mb-4" data-testid="detail-allergy-alert">
                <AlertTriangle size={16} className="text-[#f59e0b] shrink-0 mt-0.5" />
                <div className="text-[12px] leading-relaxed text-[#92400e]">
                  <p className="font-bold mb-0.5">Attenzione, controlla il tuo profilo!</p>
                  <ul className="space-y-0.5">
                    {warnings.map((w, i) => (
                      <li key={`dwarn-${i}`}>• {w}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {editing ? (
              <div className="mb-4 rounded-2xl border border-[#bfe0fb] bg-[#f0f7ff] p-3.5" data-testid="edit-meal-form">
                <span className="text-[10px] uppercase tracking-wide text-[#2b90ef] font-bold block mb-2.5">Correggi i valori</span>
                <div className="grid grid-cols-2 gap-2.5">
                  <EditField label="Calorie (kcal)" testid="edit-calories" value={form.calories} onChange={(v) => setField("calories", v)} />
                  {isDrink ? (
                    <>
                      <EditField label="Alcol (g)" testid="edit-alcohol" value={form.alcohol_g} onChange={(v) => setField("alcohol_g", v)} />
                      <EditField label="Zuccheri (g)" testid="edit-carbs" value={form.carbs} onChange={(v) => setField("carbs", v)} />
                    </>
                  ) : (
                    <>
                      <EditField label="Proteine (g)" testid="edit-protein" value={form.protein} onChange={(v) => setField("protein", v)} />
                      <EditField label="Carboidrati (g)" testid="edit-carbs" value={form.carbs} onChange={(v) => setField("carbs", v)} />
                      <EditField label="Grassi (g)" testid="edit-fat" value={form.fat} onChange={(v) => setField("fat", v)} />
                    </>
                  )}
                </div>
                <div className="mt-2.5">
                  <label className="block text-[9px] uppercase tracking-wide text-slate-400 font-bold mb-1">Nota (facoltativa)</label>
                  <input
                    type="text"
                    maxLength={120}
                    value={form.note}
                    onChange={(e) => setField("note", e.target.value)}
                    data-testid="edit-note"
                    placeholder="Es. post-allenamento, cena fuori..."
                    className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-sm text-slate-800 outline-none focus:border-[#2b90ef]"
                  />
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={saveEdit} data-testid="save-edit-btn" className="flex-1 py-2.5 bg-[#2b90ef] text-white rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform flex items-center justify-center gap-1.5">
                    <Check size={15} /> Salva
                  </button>
                  <button onClick={() => setEditing(false)} data-testid="cancel-edit-btn" className="px-4 py-2.5 bg-white border border-slate-200 text-slate-500 rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform flex items-center justify-center gap-1.5">
                    <X size={15} /> Annulla
                  </button>
                </div>
              </div>
            ) : (
              <>
                {isDrink ? (
                  <div className="flex gap-2 mb-2.5">
                    <MacroPill Icon={Wine} label="Alcol" value={meal.alcohol_g || 0} color="#2b90ef" />
                    <MacroPill Icon={Wheat} label="Zuccheri" value={meal.carbs || 0} color="#f59e0b" />
                  </div>
                ) : (
                  <div className="flex gap-2 mb-2.5">
                    <MacroPill Icon={Beef} label="Prot" value={meal.protein || 0} color="#ef4444" />
                    <MacroPill Icon={Wheat} label="Carbo" value={meal.carbs || 0} color="#f59e0b" />
                    <MacroPill Icon={Droplet} label="Grassi" value={meal.fat || 0} color="#3b82f6" />
                  </div>
                )}
                <button onClick={startEdit} data-testid="edit-meal-btn" className="w-full mb-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 text-[12px] font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform hover:border-[#2b90ef] hover:text-[#2b90ef]">
                  <Pencil size={13} /> Modifica valori
                </button>
              </>
            )}

            {meal.ingredients?.length > 0 && (
              <div className="mb-4">
                <span className="text-[10px] uppercase tracking-wide text-slate-400 font-bold block mb-2">Ingredienti</span>
                <div className="flex flex-wrap gap-1.5">
                  {meal.ingredients.map((ing, i) => (
                    <span key={`${ing}-${i}`} className="text-[11px] bg-slate-50 border border-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">{ing}</span>
                  ))}
                </div>
              </div>
            )}

            {meal.tip && (
              <div className="text-xs text-[#78350f] bg-amber-50 border-l-[3px] border-[#f59e0b] rounded-md px-3 py-2.5 leading-relaxed mb-4">
                💡 {meal.tip}
              </div>
            )}

            {meal.note && !editing && (
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5 leading-relaxed mb-4 flex items-start gap-2" data-testid="meal-note-display">
                <StickyNote size={14} className="text-[#f59e0b] shrink-0 mt-0.5" />
                <span>{meal.note}</span>
              </div>
            )}

            {!isDrink && (
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => onOpenRecipe?.(meal)}
                  data-testid="detail-get-recipe-btn"
                  className="flex-1 py-3 bg-[#2b90ef] hover:bg-[#1d78cc] text-white rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
                >
                  <ChefHat size={16} /> Ottieni Ricetta
                </button>
                <button
                  onClick={toggleFav}
                  disabled={saving}
                  data-testid="detail-favorite-btn"
                  aria-label="Salva tra i preferiti"
                  className="px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center active:scale-90 transition-transform disabled:opacity-50"
                >
                  <Star size={20} className={fav ? "fill-[#f59e0b] text-[#f59e0b]" : "text-slate-300"} />
                </button>
              </div>
            )}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
